import { useEffect, useMemo } from 'react';
import { getServerTimeApi } from '../apis';
import { createClockSync, monotonicNow } from '../lib/clockSync';
import { useNetwork } from './useNetwork';

const RESYNC_MS = 10 * 60 * 1000;

// The last measured offset survives a reboot: an offline tile starts close to the others.
const STORAGE_KEY = 'pixelplay-clock';

const loadOffset = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return Number.isFinite(value?.offsetMs) ? value.offsetMs : null;
  } catch {
    return null;
  }
};

const saveOffset = (offsetMs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ offsetMs }));
  } catch {
    // Storage full or blocked: the next sync measures it again.
  }
};

/**
 * A server-synced clock for video wall tiles. Samples GET Screen/time on start, every 10 minutes
 * and when the network returns; offline it keeps the last offset. Returns a stable `now()` (epoch
 * ms). `seedOffsetMs` (the bundle's rough offset vs. Date.now) is used until the first sync.
 */
export const useSyncedClock = ({ enabled, seedOffsetMs }) => {
  const clock = useMemo(() => {
    const stored = loadOffset();
    // The bundle's offset is against Date.now; express it on the monotonic clock.
    const seed = stored ?? (seedOffsetMs || 0) + (Date.now() - monotonicNow());
    return createClockSync({
      seedOffsetMs: seed,
      fetchServerMs: async () => (await getServerTimeApi()).data.utcMs,
    });
  }, []);

  const sync = async () => {
    if (enabled && (await clock.sync())) saveOffset(clock.offset());
  };

  useEffect(() => {
    if (!enabled) return;
    sync();
    const timer = setInterval(sync, RESYNC_MS);
    return () => clearInterval(timer);
  }, [enabled]);

  useNetwork({ onOnline: sync });

  return clock.now;
};
